import { useEffect, useState } from 'react'
import { api } from '../api/client'
import { fetchMe } from '../api/auth'
import { Button, Callout, Dialog, FormGrid, IconButton, TextField } from './ui'

/**
 * F-096 (REQ-257-001): pulsante "Clona" con modale per le pagine di configurazione. Il clone nasce sempre
 * inattivo o in Bozza (lo decide il server) e non viene mai attivato da qui. La clonazione è riservata
 * all'Admin: il pulsante compare solo se la sessione è Admin; il server rifà comunque il controllo (403).
 */
export interface CloneField {
  key: string
  label: string
  type?: 'text' | 'date'
  required?: boolean
  placeholder?: string
}

export interface CloneConfigurationButtonProps {
  /** Nome dell'elemento nel titolo della modale, ad esempio "campagna". */
  entityLabel: string
  /** URL completo, ad esempio `/api/campaigns/12/clone`. */
  url: string
  /** Nome proposto per la copia. */
  defaultName: string
  /** Campi in più oltre al nome (date della gara, codice del modello, ...). */
  extraFields?: CloneField[]
  /** Nota sotto i campi: cosa si copia e cosa no. */
  note?: string
  /** Chiamata con l'id della copia appena creata: la pagina la apre. */
  onCloned: (newId: number) => void | Promise<void>
}

/**
 * Dopo una clonazione la pagina ricarica l'elenco e apre la copia: restituisce la funzione da chiamare con
 * l'id appena creato; appena l'elemento compare nell'elenco viene passato a `open` (una sola volta).
 */
export function useOpenAfterClone<T extends { id: number }>(items: T[], open: (item: T) => void) {
  const [pending, setPending] = useState<number | null>(null)
  useEffect(() => {
    if (pending === null) return
    const item = items.find(i => i.id === pending)
    if (item) {
      setPending(null)
      open(item)
    }
  }, [pending, items, open])
  return setPending
}

let adminPromise: Promise<boolean> | null = null

function isAdminSession(): Promise<boolean> {
  adminPromise ??= fetchMe()
    .then(me => me !== null && (me.role === 'Administrator' || me.role === 'EqualAdmin'))
    .catch(() => false)
  return adminPromise
}

export function CloneConfigurationButton(props: CloneConfigurationButtonProps) {
  const { entityLabel, url, defaultName, extraFields = [], note, onCloned } = props
  const [admin, setAdmin] = useState(false)
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [extra, setExtra] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    void isAdminSession().then(v => { if (alive) setAdmin(v) })
    return () => { alive = false }
  }, [])

  if (!admin) return null

  function openModal() {
    setName(`${defaultName} (copia)`)
    setExtra({})
    setError(null)
    setOpen(true)
  }

  async function submit() {
    setError(null)
    if (!name.trim()) { setError('Il nome è obbligatorio.'); return }
    const missing = extraFields.find(f => f.required && !(extra[f.key] ?? '').trim())
    if (missing) { setError(`${missing.label} è obbligatorio.`); return }
    setBusy(true)
    try {
      const body: Record<string, string> = { name: name.trim() }
      for (const f of extraFields) {
        const v = (extra[f.key] ?? '').trim()
        if (v) body[f.key] = v
      }
      const res = await api<{ id: number }>(url, { method: 'POST', body: JSON.stringify(body) })
      setOpen(false)
      await onCloned(res.id)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Clonazione non riuscita.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <IconButton icon="content_copy" size="sm" aria-label="Clona" onClick={openModal} />
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title={`Clona ${entityLabel}`}
        supportingText={note ?? 'La copia nasce inattiva o in bozza: va attivata a mano dopo averla controllata.'}
        dismissible={!busy}
        as="form"
        onSubmit={e => {
          e.preventDefault()
          void submit()
        }}
        actions={
          <>
            <Button variant="text" onClick={() => setOpen(false)}>
              Annulla
            </Button>
            <Button type="submit" variant="text" loading={busy}>
              {busy ? 'Clonazione...' : 'Clona'}
            </Button>
          </>
        }
      >
        {error && <Callout tone="error">{error}</Callout>}
        <FormGrid columns={1}>
          <TextField label="Nome della copia" value={name} onChange={e => setName(e.target.value)} autoFocus />
          {extraFields.map(f => (
            <TextField
              key={f.key}
              label={`${f.label}${f.required ? ' *' : ''}`}
              type={f.type ?? 'text'}
              placeholder={f.placeholder}
              value={extra[f.key] ?? ''}
              onChange={e => setExtra(prev => ({ ...prev, [f.key]: e.target.value }))}
            />
          ))}
        </FormGrid>
      </Dialog>
    </>
  )
}
